import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@app/core/shared/auth.service';
import { environment } from '@env/environment';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  mergeMap,
  Observable,
  switchMap,
  take, tap,
  throwError,
} from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);

  private isRefreshing = false;

  private refreshDone$ = new BehaviorSubject<boolean>(true);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldSkip(req)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.refreshAndRetry(req, next);
        }
        return throwError(() => err);
      }),
      mergeMap((event) => {
        if (event instanceof HttpResponse && this.isGraphQLErrorAuth(event)) {
          return this.refreshAndRetry(req, next);
        }
        return [ event ];
      }),
    );
  }

  private refreshAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshDone$.next(false);

      return this.authService.refreshToken().pipe(
        switchMap(() => {
          this.refreshDone$.next(true);
          return next.handle(req);
        }),
        catchError((error) => {
          this.refreshDone$.next(true);
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      );
    }

    return this.refreshDone$.pipe(
      filter((done) => done === true),
      take(1),
      switchMap(() => next.handle(req)),
    );
  }

  private shouldSkip(req: HttpRequest<any>): boolean {
    const { body, url } = req;
    const isGqlRequest = url === `${environment.apiUrl}/graphql`;

    return !isGqlRequest || ['dropMe', 'refreshToken', 'signIn', 'signUp'].includes(body?.operationName);
  }

  private isGraphQLErrorAuth(res: HttpResponse<any>): boolean {
    const errors = res.body?.errors;

    return Array.isArray(errors) && errors.some((err: any) =>
      err.extensions?.code === 'UNAUTHENTICATED' ||
      err.extensions?.code === 'TOKEN_EXPIRED' ||
      err.message === 'Unauthorized',
    );
  }
}
